/**
 * FO-023 (batch-15) — benoemde ontbrekende-data-melding voor de IC-periode-
 * simulatie/berekening.
 *
 * Vervangt de kale fout ("Niet alle transacties zijn klaar" / "Status-overgang
 * niet toegestaan") door een leesbare melding per transactie: welke regel/grondslag
 * ontbreekt, in de stijl van de IC-vooruitblik-waarschuwingen. Pure functie (geen
 * Prisma); de caller levert de labels aan.
 *
 * Voorbeeld:
 *   "IC-2026-06-0042 (Tankpas, bestuurder Jan Jansen, STREEP Personeel → STREEP Service):
 *    geen brandstoftransacties in de periode."
 */

import { IC_RULE_TYPE_LABELS, IC_TRANSACTION_STATUS_LABELS } from '@streep/types'
import type { ICRuleType, ICTransactionStatus } from '@streep/types'

export interface ICOntbrekendeDataInput {
  code: string
  type: ICRuleType
  status: ICTransactionStatus
  bronBvLabel: string
  gebruikendeBvLabel: string
  /** Uitgeleende medewerker (MEDEWERKER) of bestuurder (AUTO/TANKPAS), indien bekend. */
  bronGebruikerLabel?: string | null
  /** De opgeslagen blokkadeReden (de concrete grondslag), indien gezet. */
  blokkadeReden?: string | null
}

function rolVoorType(type: ICRuleType): string {
  if (type === 'AUTO' || type === 'TANKPAS') return 'bestuurder'
  if (type === 'MEDEWERKER') return 'medewerker'
  return 'gebruiker'
}

function redenVoorStatus(status: ICTransactionStatus): string {
  switch (status) {
    case 'TE_BEREKENEN':
      return 'is nog niet berekend.'
    case 'BEREKEND':
      return 'is berekend maar nog niet ter keuring aangeboden.'
    case 'TE_KEUREN':
      return 'wacht nog op goedkeuring.'
    case 'WACHT_OP_LEASEPRIJS':
      return 'wacht op de leaseprijs van het voertuig.'
    case 'WACHT_OP_OMZETCIJFERS':
      return 'wacht op omzetcijfers (grondslag van de management fee).'
    case 'WACHT_OP_GOEDKEURING_UREN':
      return 'wacht op goedkeuring van de uren.'
    case 'WACHT_OP_VERDEELSLEUTEL':
      return 'wacht op een (kloppende) verdeelsleutel.'
    default:
      return `staat op status "${IC_TRANSACTION_STATUS_LABELS[status]}" en is nog niet goedgekeurd.`
  }
}

/** Eén transactie → benoemde ontbrekende-data-melding (geen em-dash). */
export function beschrijfICOntbrekendeData(item: ICOntbrekendeDataInput): string {
  const typeLabel = IC_RULE_TYPE_LABELS[item.type]
  const wie = item.bronGebruikerLabel
    ? `, ${rolVoorType(item.type)} ${item.bronGebruikerLabel}`
    : ''
  const paar = `${item.bronBvLabel} → ${item.gebruikendeBvLabel}`
  const reden =
    item.blokkadeReden && item.blokkadeReden.trim().length > 0
      ? item.blokkadeReden.trim()
      : redenVoorStatus(item.status)
  return `${item.code} (${typeLabel}${wie}, ${paar}): ${reden}`
}
